import Text from '@/components/content/Text'
import Banner from '@/components/content/Banner'
import { OutboundTable } from './components/OutboundTable'
import { Button, Checkbox, HStack, Icon, VStack } from '@musinsa/mwms-ds'
import useModal from '@/hooks/useModal'
import { OverseasPriorityModal } from './components/cut-off/OverSeasPriorityModal'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useCutOffDateOnly } from './hooks/useCutOffDate'
import dayjs from 'dayjs'
import { DATE_FORMAT } from '@/utils/consts'
import { Suspense } from 'react'
import { OutboundSchedulePageQueryParams, OutboundScheduleShipperCode } from './types/query'
import SelectBox from '@/components/content/SelectBox'
import { useFeatureFlagBoolean } from '@/services/feature/useFeatureQueries'
import { useScheduleShipper } from './hooks/useScheduleShipper'
import { ScheduleShipperProvider } from './context/ScheduleShipperContext'
import { SCHEDULE_SHIPPER_EMPTY_MESSAGE } from '@/pages/outbound/scheduleShipper'
import { colorSystem } from '@/styles/theme'

export default function OutboundGI11() {
  const { parsedQs, replaceQuery } = useParsedQuery<OutboundSchedulePageQueryParams>()
  const showPastCutOff = parsedQs.showPastCutOff === 'true'

  const { cutOffDate } = useCutOffDateOnly()

  // 허용 화주로 좁힌 선택지·선택값 — 확정 전에는 그리드를 열지 않는다 (LOGISPRD-12495)
  const scheduleShipper = useScheduleShipper()

  const overseasModal = useModal()

  const { data: isFulfillmentEnabled } = useFeatureFlagBoolean('fulfillment.optimization.b2c.activeShippers', 'enabled')

  const handleDateChange = (type: 'prev' | 'next') => {
    const newDate = {
      prev: dayjs(cutOffDate).subtract(1, 'day').format(DATE_FORMAT.date),
      next: dayjs(cutOffDate).add(1, 'day').format(DATE_FORMAT.date),
    }[type]

    replaceQuery({ ...parsedQs, date: newDate })
  }

  const handleHidePastCutOffChange = () => {
    replaceQuery({ ...parsedQs, showPastCutOff: showPastCutOff ? undefined : 'true' })
  }

  return (
    <>
      <VStack style={{ height: '100%', minHeight: 0 }}>
        <HStack justify="space-between" align="center" gap={16}>
          <HStack align="center" gap={16}>
            <Text kind="Headline_20_bold" style={{ whiteSpace: 'nowrap' }}>
              출고일정 관리
            </Text>
            {isFulfillmentEnabled && (
              <Banner variant="warning">
                B2C 국내 출고지시는 [출고일정관리 2.5] 메뉴에서만 변경할 수 있습니다. 이곳에서 수정한 내용은 반영되지 않으니 유의해 주세요.
              </Banner>
            )}
          </HStack>
          <Button kind="secondary" content="해외출고 관리" onClick={overseasModal.onOpenModal} />
        </HStack>

        <HStack justify="space-between" align="center" style={{ marginTop: 24 }}>
          <HStack gap={16} align="center">
            <Button size="sm" kind="secondary" style={{ padding: 6, height: 'auto' }} onClick={() => handleDateChange('prev')}>
              <Icon name="arrow-left-line" />
            </Button>
            <span style={{ fontSize: 24, fontWeight: 500 }}>{cutOffDate}</span>
            <Button size="sm" kind="secondary" style={{ padding: 6, height: 'auto' }} onClick={() => handleDateChange('next')}>
              <Icon name="arrow-right-line" />
            </Button>

            {scheduleShipper.options.length > 0 && (
              <div style={{ width: 160 }}>
                <SelectBox<OutboundScheduleShipperCode>
                  size="large"
                  options={scheduleShipper.options}
                  block
                  value={scheduleShipper.shipperCode}
                  placeholder={false}
                  onChange={(shipperCode) => replaceQuery({ ...parsedQs, shipperCode })}
                />
              </div>
            )}
          </HStack>

          <Checkbox labelText="지난 컷오프 보기" size="sm" checked={showPastCutOff} onChange={handleHidePastCutOffChange} />
        </HStack>

        {scheduleShipper.shipperCode ? (
          <ScheduleShipperProvider shipperCode={scheduleShipper.shipperCode}>
            <OutboundTable />
          </ScheduleShipperProvider>
        ) : (
          !scheduleShipper.isLoading && (
            <Text kind="Body_14_regular" color={colorSystem.text.common.tertiary} style={{ marginTop: 24 }}>
              {SCHEDULE_SHIPPER_EMPTY_MESSAGE}
            </Text>
          )
        )}
      </VStack>

      {overseasModal.open && (
        <Suspense fallback={null}>
          <OverseasPriorityModal onClose={overseasModal.onCloseModal} />
        </Suspense>
      )}
    </>
  )
}
